import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Permission,
  basisPointsToPercent,
  subscriptionPlanPath,
  toJalaliDateKey,
} from '@bookora/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  JalaliDate,
  Money,
  Num,
  Percent,
  Space,
  errorMessageFa,
} from '@bookora/ui';
import { useNavigate } from 'react-router-dom';

import { fetchMe } from '@/api/endpoints/auth';
import type { ClinicAccess, ClinicReadOnlyReason } from '@/api/types';
import { identityFromMe, useAuth, useSessionStore } from '@/auth/token-store';
import { LinkButton } from '@/components/link-button';
import { SupportButton } from '@/components/support-contact';
import type { ClinicOfferResponse } from '@/api/generated/model';
import { OFFER_COPY } from '@/features/offers/copy';
import { useDeadline } from '@/features/offers/use-deadline';
import { OfferCopy } from '@/features/offers/offer-copy';
import { OfferCountdown } from '@/features/offers/offer-countdown';
import { offersApi } from '@/features/settings/api';
import {
  settingsKeys,
  useBillingState,
  useClinicOffers,
  useSubscriptionWallet,
} from '@/features/settings/hooks';
import { useCan } from '@/permissions/use-can';

import { isPayableRefusal, isReadOnlyRefusal, readOnlyRefusalCopy } from './billing-refusal';
import { useBillingRefusal } from './billing-refusal-store';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  READ-ONLY MODE — the banner, and the 402 that explains it
 * ════════════════════════════════════════════════════════════════════════════
 *
 * When a trial ends, an invoice goes unpaid past its grace period or the clinic
 * is suspended, `BillingStateInterceptor` answers every POST/PUT/PATCH/DELETE
 * with **402** (`SUBSCRIPTION_INACTIVE`, `SUBSCRIPTION_PAST_DUE`,
 * `CLINIC_SUSPENDED`, `CLINIC_CLOSED`). Reading never stops. The interceptor's
 * own comment says a 402 "routes the panel to the billing screen" — and for a
 * long time nothing in the panel did: every Save on every screen produced a
 * toast that faded, with no word about why or where to pay. On the morning a
 * trial ended, the owner simply could not book anyone.
 *
 * Two pieces close that:
 *
 *  · `BillingRefusalWatcher` listens to the MUTATION CACHE, so every mutation
 *    in every feature is covered without any feature knowing about billing.
 *    The first 402 raises ONE persistent notification (fixed key — ten failed
 *    saves do not stack ten) whose button goes to the subscription tab, or,
 *    for someone who cannot pay, says who can.
 *  · `BillingBanner` is the standing explanation under the header. Its first
 *    source is `/auth/me`'s `activeClinicAccess`, which the server computes
 *    for EVERY role: a receptionist now learns the clinic is read-only on
 *    the first paint, not from her first failed save. A `settings.billing`
 *    holder also reads the real billing state (the same query the
 *    subscription tab uses, so paying clears it there and here in one
 *    invalidation), which is what knows about an unpaid invoice still inside
 *    its grace period. Whatever a 402 has already told this tab counts too.
 *
 *  The two identity reads are kept honest with each other: a 402 re-reads
 *  `/auth/me` (and retires the refusal if the server now says writes are
 *  allowed), and a billing state that says «paid» while the cached access
 *  still says «read-only» re-reads it as well — the owner who just paid must
 *  not keep seeing the red banner until the next tab focus.
 *
 * The banner is a prompt, never a gate: the server decides what is refused.
 */

/* -------------------------------------------------------------------------- */
/* The 402 watcher                                                            */
/* -------------------------------------------------------------------------- */

const NOTIFICATION_KEY = 'bookora-billing-read-only';

/** The `/auth/me` reason tokens that paying resolves. */
function isPayableReason(reason: ClinicReadOnlyReason | null): boolean {
  return reason === 'SUBSCRIPTION_PAST_DUE' || reason === 'SUBSCRIPTION_ENDED';
}

/**
 * Re-reads `/auth/me` into the store and hands back the clinic's access.
 * Failures are swallowed: this only refreshes a banner, and the boot's own
 * `/auth/me` handling owns every real identity error.
 */
function useRefreshIdentity(): () => Promise<ClinicAccess | null | undefined> {
  const setIdentity = useSessionStore((state) => state.setIdentity);
  return useCallback(async () => {
    try {
      const me = await fetchMe();
      setIdentity(identityFromMe(me));
      return me.activeClinicAccess;
    } catch {
      return undefined;
    }
  }, [setIdentity]);
}

export function BillingRefusalWatcher() {
  const queryClient = useQueryClient();
  const { notification } = AntdApp.useApp();
  const navigate = useNavigate();
  const { activeClinicId } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  const record = useBillingRefusal((state) => state.record);
  const clearRefusal = useBillingRefusal((state) => state.clear);
  const refreshIdentity = useRefreshIdentity();

  useEffect(() => {
    return queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'error') return;
      const error = event.action.error;
      if (!isReadOnlyRefusal(error)) return;

      record({ clinicId: activeClinicId, code: error.code, message: errorMessageFa(error) });
      // The banner's sources of truth; a fresh read replaces a five-minute-old
      // «فعال» with the state that just refused the save. If the server now
      // says writes are allowed after all (the owner paid a moment ago), the
      // refusal is history.
      void queryClient.invalidateQueries({ queryKey: settingsKeys.billing(activeClinicId) });
      void refreshIdentity().then((access) => {
        if (access?.writesAllowed && access.clinicId === activeClinicId) clearRefusal();
      });

      const copy = readOnlyRefusalCopy(error.code, errorMessageFa(error), { canBilling });
      notification.error({
        key: NOTIFICATION_KEY,
        // Persistent: the user must be able to read it and act on it, and the
        // toast every screen already raises is the fading half of this.
        duration: 0,
        title: 'این تغییر ذخیره نشد',
        description: copy.description,
        actions:
          copy.action === 'pay' ? (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                notification.destroy(NOTIFICATION_KEY);
                void navigate('/settings/subscription');
              }}
            >
              تمدید اشتراک
            </Button>
          ) : copy.action === 'support' ? (
            <SupportButton size="small" />
          ) : null,
      });
    });
  }, [
    queryClient,
    notification,
    navigate,
    activeClinicId,
    canBilling,
    record,
    clearRefusal,
    refreshIdentity,
  ]);

  return null;
}

/* -------------------------------------------------------------------------- */
/* The banner                                                                 */
/* -------------------------------------------------------------------------- */

/** How close to the end of a trial the reminder starts. */
const TRIAL_WARNING_DAYS = 5;
const TRIAL_DISMISS_KEY = 'bookora_trial_banner_dismissed';

/** The dismissable kinds, each remembered on its own («فردا یادآوری کن» per kind, per clinic, per day). */
type DismissableKind = 'trial' | 'raas-trial' | 'raas-cap' | 'raas-wallet';

function dismissKey(kind: DismissableKind): string {
  // The flat trial keeps its original key, so a reminder dismissed before
  // this build stays dismissed for the rest of today.
  return kind === 'trial' ? TRIAL_DISMISS_KEY : `bookora_billing_banner_dismissed:${kind}`;
}

function readDismissals(): Partial<Record<DismissableKind, string | null>> {
  const out: Partial<Record<DismissableKind, string | null>> = {};
  for (const kind of ['trial', 'raas-trial', 'raas-cap', 'raas-wallet'] as const) {
    try {
      out[kind] = localStorage.getItem(dismissKey(kind));
    } catch {
      out[kind] = null;
    }
  }
  return out;
}

type Banner =
  | {
      kind: 'read-only';
      message: string;
      payable: boolean;
      /**
       * `message` is the server's full sentence (`readOnlyReasonFa`), which
       * already says what still works; otherwise it is a short reason and
       * the banner adds that sentence itself.
       */
      explained: boolean;
    }
  | { kind: 'past-due'; message: string }
  /** A RaaS clinic past due: its wallet is in debt beyond the allowed floor. */
  | { kind: 'raas-overdraft'; graceEndsAt: string | null }
  | { kind: 'offer'; offer: ClinicOfferResponse }
  | { kind: 'trial'; days: number }
  | { kind: 'raas-trial'; endsAt: string }
  | { kind: 'raas-cap'; full: boolean; capPercentBp: number; capRials: number }
  | { kind: 'raas-wallet'; empty: boolean; days: number | null; floorRials: number };

/**
 * The standing billing sentence under the header — one at a time, the most
 * urgent first:
 *
 *   read-only › past due (flat invoice / RaaS wallet debt) › sales offer ›
 *   trial ending ›
 *   RaaS cap (80% warning, 100% info) › RaaS wallet (low, empty).
 *
 * The RaaS kinds come from `ClinicBillingStateResponse.raas.alert`, which the
 * server computes; they are for `settings.billing` holders only, like the
 * trial countdown — a nurse has nothing to do with the news. Every kind but
 * read-only and past-due has «فردا یادآوری کن», remembered per kind, per
 * clinic, per day; a dismissed kind lets the next one through.
 */
export function BillingBanner() {
  const { activeClinic, activeClinicId, impersonatedBy, activeClinicAccess: access } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  const billing = useBillingState();
  const refusal = useBillingRefusal((state) => state.refusal);
  const clearRefusal = useBillingRefusal((state) => state.clear);
  const refreshIdentity = useRefreshIdentity();

  // The sales offer. Read only for a billing holder who is not impersonated;
  // «فعلاً نه» goes to the server (not localStorage) so it follows the clinic
  // across devices. `seen` starts the offer's personal window and is sent once
  // per grant, only while the offer banner is the one on screen.
  const queryClient = useQueryClient();
  const offers = useClinicOffers({ enabled: !impersonatedBy });
  const seenGrants = useRef(new Set<string>());
  const [dismissedGrants, setDismissedGrants] = useState<ReadonlySet<string>>(new Set());
  const [expiredGrants, setExpiredGrants] = useState<ReadonlySet<string>>(new Set());
  const expireOffer = useCallback(
    (grantId: string) => {
      setExpiredGrants((previous) => new Set(previous).add(grantId));
      void queryClient.invalidateQueries({ queryKey: settingsKeys.offers(activeClinicId) });
    },
    [queryClient, activeClinicId],
  );
  const dismissOffer = useMutation({
    mutationFn: (grantId: string) => offersApi.dismiss(grantId),
    onMutate: (grantId) => setDismissedGrants((previous) => new Set(previous).add(grantId)),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: settingsKeys.offers(activeClinicId) }),
  });

  // «Remind me tomorrow», per clinic. The read-only banner has no dismiss:
  // hiding the one sentence that explains why nothing saves helps nobody.
  // The tokens read from storage are compared on every render rather than
  // folded into a boolean at mount: a clinic switch (or a panel left open past
  // midnight) changes the token, and the reminder must come back for it.
  const dismissToken = `${activeClinicId ?? ''}:${toJalaliDateKey(new Date())}`;
  const [dismissed, setDismissed] = useState(readDismissals);
  const isDismissed = (kind: DismissableKind): boolean => dismissed[kind] === dismissToken;

  const state = billing.data;
  const billingSaysWritable = Boolean(canBilling && state && !state.readOnly);
  // The overdraft banner states the debt; the wallet is read only for it.
  const raasPastDue =
    canBilling &&
    state?.subscription?.billingModel === 'RAAS' &&
    state.subscription.status === 'PAST_DUE';
  const wallet = useSubscriptionWallet({ enabled: Boolean(raasPastDue) });

  // A fresh billing read that says writes are allowed again (the owner just
  // paid, maybe in another tab) retires whatever an earlier 402 said…
  useEffect(() => {
    if (billingSaysWritable && refusal) clearRefusal();
  }, [billingSaysWritable, refusal, clearRefusal]);
  // …and re-reads `/auth/me` if the cached access still says «read-only»,
  // rather than leaving the red banner up until the next tab focus.
  const accessSaysReadOnly = access?.writesAllowed === false;
  useEffect(() => {
    if (billingSaysWritable && accessSaysReadOnly) void refreshIdentity();
  }, [billingSaysWritable, accessSaysReadOnly, refreshIdentity]);

  // A super-admin acting as this user is exempt from the 402 by design (they
  // are usually here to fix the account) and has their own banner already.
  if (impersonatedBy || !activeClinic) return null;

  const banner =
    resolveBanners().find(
      (candidate) =>
        !(
          candidate.kind === 'trial' ||
          candidate.kind === 'raas-trial' ||
          candidate.kind === 'raas-cap' ||
          candidate.kind === 'raas-wallet'
        ) || !isDismissed(candidate.kind),
    ) ?? null;
  if (!banner) return null;

  function dismiss(kind: DismissableKind): void {
    try {
      localStorage.setItem(dismissKey(kind), dismissToken);
    } catch {
      // Not remembered; it simply comes back on the next load.
    }
    setDismissed((previous) => ({ ...previous, [kind]: dismissToken }));
  }

  /** Every banner that applies, most urgent first. */
  function resolveBanners(): Banner[] {
    const clinicRefusal = refusal && refusal.clinicId === activeClinicId ? refusal : null;
    const clinicSuspended =
      activeClinic?.clinicStatus === 'SUSPENDED' || activeClinic?.clinicStatus === 'CLOSED';

    // 1. The server's word for every role. Skipped only when the owner's
    //    fresher billing read already says «paid» (the refresh above is on
    //    its way to catch the access up).
    if (access && !access.writesAllowed && !billingSaysWritable) {
      return [
        {
          kind: 'read-only',
          message: access.readOnlyReasonFa ?? clinicRefusal?.message ?? 'اشتراک کلینیک فعال نیست',
          payable: isPayableReason(access.readOnlyReason),
          explained: access.readOnlyReasonFa !== null,
        },
      ];
    }

    if (canBilling && state) {
      if (state.readOnly) {
        return [
          {
            kind: 'read-only',
            message: state.readOnlyReason ?? clinicRefusal?.message ?? 'اشتراک کلینیک فعال نیست',
            payable: !clinicSuspended,
            explained: false,
          },
        ];
      }
      const subscription = state.subscription;
      const raas = subscription?.billingModel === 'RAAS';
      if (subscription?.status === 'PAST_DUE') {
        return [
          raas
            ? { kind: 'raas-overdraft', graceEndsAt: subscription.graceEndsAt ?? null }
            : { kind: 'past-due', message: 'صورت‌حساب اشتراک پرداخت نشده' },
        ];
      }
      const out: Banner[] = [];
      // The conversion lever sits above the trial reminder; read-only and
      // past-due have already returned.
      const spotlight = offers.data?.spotlight;
      if (
        spotlight &&
        !spotlight.dismissed &&
        !dismissedGrants.has(spotlight.grantId) &&
        !expiredGrants.has(spotlight.grantId)
      ) {
        out.push({ kind: 'offer', offer: spotlight });
      }
      const days = access?.trialDaysRemaining ?? subscription?.trialDaysRemaining;
      if (
        (subscription?.status === 'TRIALING' || typeof access?.trialDaysRemaining === 'number') &&
        typeof days === 'number' &&
        days <= TRIAL_WARNING_DAYS
      ) {
        out.push(
          raas && subscription?.trialEndsAt
            ? { kind: 'raas-trial', endsAt: subscription.trialEndsAt }
            : { kind: 'trial', days: Math.max(0, days) },
        );
      }
      const slice = state.raas ?? null;
      if (slice?.alert === 'CAP_80' || slice?.alert === 'CAP_100') {
        out.push({
          kind: 'raas-cap',
          full: slice.alert === 'CAP_100',
          capPercentBp: slice.capPercentBp,
          capRials: slice.capRials,
        });
      }
      if (
        slice?.alert === 'WALLET_LOW' ||
        slice?.alert === 'WALLET_CRITICAL' ||
        slice?.alert === 'WALLET_EMPTY'
      ) {
        out.push({
          kind: 'raas-wallet',
          empty: slice.alert === 'WALLET_EMPTY',
          days: slice.walletDaysOfCover ?? null,
          floorRials: slice.overdraftFloorRials,
        });
      }
      return out;
    }

    if (clinicRefusal) {
      return [
        {
          kind: 'read-only',
          message: clinicRefusal.message,
          payable: isPayableRefusal(clinicRefusal.code),
          explained: false,
        },
      ];
    }
    // The membership's own status, for when `/auth/me` has not said yet.
    if (!access && activeClinic?.clinicStatus === 'SUSPENDED') {
      return [
        {
          kind: 'read-only',
          message: 'حساب کلینیک موقتاً غیرفعال است',
          payable: false,
          explained: false,
        },
      ];
    }
    if (activeClinic?.clinicStatus === 'PAST_DUE') {
      return [{ kind: 'past-due', message: 'اشتراک کلینیک پرداخت نشده' }];
    }
    // The trial countdown is only for someone who can act on it: a nurse
    // told «۳ روز مونده» every morning has nothing to do with the news.
    if (canBilling && typeof access?.trialDaysRemaining === 'number') {
      const days = access.trialDaysRemaining;
      if (days <= TRIAL_WARNING_DAYS) return [{ kind: 'trial', days: Math.max(0, days) }];
    }
    return [];
  }

  /** «Not today» is a labelled button, not a bare ✕: a ✕ reads as «gone for good». */
  const remindTomorrow = (kind: DismissableKind) => (
    <Button type="text" size="small" onClick={() => dismiss(kind)}>
      فردا یادآوری کن
    </Button>
  );

  if (banner.kind === 'offer') {
    const o = banner.offer;
    return (
      <OfferBanner
        offer={o}
        seenGrants={seenGrants.current}
        onDismiss={() => dismissOffer.mutate(o.grantId)}
        onExpire={() => expireOffer(o.grantId)}
      />
    );
  }

  if (banner.kind === 'read-only') {
    return (
      <Alert
        className="bk-billing-banner"
        type="error"
        showIcon
        banner
        role="alert"
        title={
          banner.explained
            ? 'ثبت و ویرایش غیرفعاله؛ می‌تونید اطلاعات قبلی رو ببینید'
            : `${banner.message} — اطلاعات فقط قابل مشاهده‌اند`
        }
        description={
          banner.explained
            ? banner.message
            : 'پرونده‌ها و گزارش‌ها رو می‌بینید، ولی ثبت نوبت، جلسه یا هر تغییر جدیدی تا فعال شدن دوبارهٔ حساب ممکن نیست.'
        }
        action={
          banner.payable && canBilling ? (
            <LinkButton type="primary" size="small" to="/settings/subscription">
              پرداخت و فعال‌سازی
            </LinkButton>
          ) : banner.payable ? (
            <span className="bk-billing-banner__note">به مالک کلینیک خبر بدید</span>
          ) : (
            <SupportButton size="small" />
          )
        }
      />
    );
  }

  if (banner.kind === 'past-due') {
    return (
      <Alert
        className="bk-billing-banner"
        type="warning"
        showIcon
        banner
        title={banner.message}
        description={
          canBilling
            ? 'تا پایان مهلت پرداخت می‌تونید اطلاعات رو ثبت و ویرایش کنید. بعد از اون، فقط امکان مشاهدهٔ اطلاعات دارید.'
            : 'اگر صورت‌حساب پرداخت نشه، ثبت و ویرایش اطلاعات غیرفعال می‌شه. به مالک کلینیک خبر بدید.'
        }
        action={
          canBilling ? (
            <LinkButton type="primary" size="small" to="/settings/subscription">
              پرداخت صورت‌حساب
            </LinkButton>
          ) : null
        }
      />
    );
  }

  if (banner.kind === 'raas-overdraft') {
    // Never a signed amount: the debt is said as «بدهکار» with its absolute value.
    const balance = wallet.data?.balanceRials;
    return (
      <Alert
        className="bk-billing-banner"
        type="warning"
        showIcon
        banner
        title={
          typeof balance === 'number' && balance < 0 ? (
            <>
              کیف پول اشتراک <Money rials={Math.abs(balance)} colorNegative={false} /> بدهکار است
            </>
          ) : (
            'کیف پول اشتراک بدهکار است'
          )
        }
        description={
          banner.graceEndsAt ? (
            <>
              تا <JalaliDate value={banner.graceEndsAt} withRelative={false} /> اون رو شارژ کنید تا
              ثبت اطلاعات متوقف نشه.
            </>
          ) : (
            'کیف پول اشتراک رو شارژ کنید تا ثبت اطلاعات متوقف نشه.'
          )
        }
        action={
          <LinkButton type="primary" size="small" to="/settings/results?focus=wallet">
            شارژ کیف پول
          </LinkButton>
        }
      />
    );
  }

  if (banner.kind === 'raas-trial') {
    return (
      <Alert
        className="bk-billing-banner"
        type="info"
        showIcon
        banner
        title={
          <>
            معافیت هزینهٔ ثابت نتیجه‌محور تا{' '}
            <JalaliDate value={banner.endsAt} withRelative={false} />
          </>
        }
        // Only the platform fee waits for the trial's end: patients past the
        // trial's free quantity are charged during it (owner round 4).
        description="بعد از این تاریخ، هزینهٔ ثابت ماهانه هم از کیف پول اشتراک کسر می‌شه. هزینهٔ بیماران بیشتر از سهم رایگان، در این دوره هم محاسبه می‌شه. کیف پول رو قبل از پایان دوره شارژ کنید."
        action={
          <Space size={4} wrap>
            <LinkButton type="primary" size="small" to="/settings/results?focus=wallet">
              شارژ کیف پول
            </LinkButton>
            {remindTomorrow('raas-trial')}
          </Space>
        }
      />
    );
  }

  if (banner.kind === 'raas-cap') {
    return (
      <Alert
        className="bk-billing-banner"
        type={banner.full ? 'info' : 'warning'}
        showIcon
        banner
        title={
          banner.full ? (
            'هزینهٔ این ماه به سقف رسیده است؛ بابت بیماران بعدی هزینه‌ای اضافه نمی‌شه'
          ) : (
            <>
              هزینهٔ نتیجه‌محور این ماه به{' '}
              <Percent value={basisPointsToPercent(banner.capPercentBp)} decimals={0} /> سقف رسید
            </>
          )
        }
        description={
          banner.full ? undefined : (
            <>
              هزینهٔ نتیجه‌محور این ماه حداکثر <Money rials={banner.capRials} /> است و از این سقف
              بیشتر نمی‌شه.
            </>
          )
        }
        action={
          <Space size={4} wrap>
            <LinkButton type="primary" size="small" to="/settings/results">
              نتایج و صورت‌حساب
            </LinkButton>
            {remindTomorrow('raas-cap')}
          </Space>
        }
      />
    );
  }

  if (banner.kind === 'raas-wallet') {
    return (
      <Alert
        className="bk-billing-banner"
        type="warning"
        showIcon
        banner
        title={
          banner.empty ? (
            'موجودی کیف پول اشتراک تموم شده'
          ) : banner.days !== null ? (
            <>
              موجودی کیف پول اشتراک برای حدود <Num value={banner.days} suffix="روز" /> دیگه کافیه
            </>
          ) : (
            'موجودی کیف پول اشتراک داره تموم می‌شه'
          )
        }
        description={
          banner.empty ? (
            <>
              کار کلینیک ادامه داره؛ تا <Money rials={Math.abs(banner.floorRials)} /> بدهی موقت مجاز
              است و بعد از اون مهلت پرداخت شروع می‌شه.
            </>
          ) : undefined
        }
        action={
          <Space size={4} wrap>
            <LinkButton type="primary" size="small" to="/settings/results?focus=wallet">
              شارژ کیف پول
            </LinkButton>
            {remindTomorrow('raas-wallet')}
          </Space>
        }
      />
    );
  }

  return (
    <Alert
      className="bk-billing-banner"
      type="warning"
      showIcon
      banner
      title={
        banner.days === 0 ? (
          'دورهٔ آزمایشی امروز تمام می‌شه'
        ) : (
          <>
            <Num value={banner.days} /> روز از دورهٔ آزمایشی رایگان مونده
          </>
        )
      }
      description="برای ادامهٔ ثبت و ویرایش بعد از دورهٔ آزمایشی، یک پلن انتخاب کنید."
      action={
        <Space size={4} wrap>
          {/* Straight to the plan list, not the top of the billing screen. */}
          <LinkButton type="primary" size="small" to={subscriptionPlanPath(null)}>
            انتخاب پلن
          </LinkButton>
          {remindTomorrow('trial')}
        </Space>
      }
    />
  );
}

/** The offer banner. Mounted only while it is the one rendered, so `seen` fires only then. */
function OfferBanner({
  offer,
  seenGrants,
  onDismiss,
  onExpire,
}: {
  offer: ClinicOfferResponse;
  seenGrants: Set<string>;
  onDismiss: () => void;
  onExpire: () => void;
}) {
  const { expired } = useDeadline(offer.expiresAt);
  useEffect(() => {
    if (expired) onExpire();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire on the flip only
  }, [expired]);
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();
  useEffect(() => {
    if (seenGrants.has(offer.grantId)) return;
    seenGrants.add(offer.grantId);
    void offersApi
      .seen(offer.grantId, 'panel')
      .then(() => queryClient.invalidateQueries({ queryKey: settingsKeys.offers(activeClinicId) }))
      .catch(() => {
        // Counting a view is best-effort; the banner stays.
      });
  }, [offer.grantId, seenGrants, queryClient, activeClinicId]);

  return (
    <Alert
      className="bk-billing-banner"
      type="success"
      showIcon
      banner
      title={<OfferCopy offer={offer} text={offer.headline} />}
      description={<OfferCountdown expiresAt={offer.expiresAt} />}
      action={
        <Space size={4} wrap>
          <LinkButton
            type="primary"
            size="small"
            to={`/settings/subscription?offer=${encodeURIComponent(offer.grantId)}`}
          >
            {OFFER_COPY.claim}
          </LinkButton>
          <Button type="text" size="small" onClick={onDismiss}>
            {OFFER_COPY.notNow}
          </Button>
        </Space>
      }
    />
  );
}
